import moment from "moment";
import { getModelTypeLabel } from "~/lib/utils";
import type { INotification } from "~/routes/portal/type";

// NotificationType: 0=ActionAssignment, 1=AlertReminder, 2=ApprovalRequest, 3=ApprovalRework, 4=RecordAssigned
export type TypeMeta = {
  label: string;
  icon: string;
  rail: string;
  pill: string;
};

const NEUTRAL_META: TypeMeta = {
  label: "Notification",
  icon: "Bell",
  rail: "bg-neutral-300",
  pill: "bg-neutral-200 text-neutral-600",
};

// warning-100 is a broken token in tailwind.config.ts; reminders use warning-200 / accent-200 deliberately.
export function metaFor(item: INotification): TypeMeta {
  switch (item.notificationType) {
    case 0:
      return { label: "Action assigned", icon: "ClipboardList", rail: "bg-info-500", pill: "bg-info-100 text-info-900" };
    case 1: {
      const overdue = item.dueDate ? moment(item.dueDate).isBefore(moment()) : false;
      return overdue
        ? { label: "Overdue", icon: "AlarmClock", rail: "bg-danger-500", pill: "bg-danger-500 text-white" }
        : { label: "Due soon", icon: "Clock", rail: "bg-accent-500", pill: "bg-accent-200 text-accent-900" };
    }
    case 2:
      return { label: "Approval needed", icon: "ShieldCheck", rail: "bg-warning-500", pill: "bg-warning-200 text-warning-800" };
    case 3:
      return { label: "Needs rework", icon: "RotateCcw", rail: "bg-danger-400", pill: "bg-danger-100 text-danger-600" };
    case 4:
      return { label: `New ${getModelTypeLabel(item.modelType) || "Record"}`, icon: "FilePlus2", rail: "bg-success-500", pill: "bg-success-100 text-success-700" };
    default:
      return NEUTRAL_META;
  }
}

// Every notification concerns an Action (modelId is the action id, not the parent record id),
// so we route to the module's list area rather than a record detail URL (which would 404 with an action id).
const MODULE_ROUTES: Record<string, string> = {
  action: "/hse/actions",
  incident: "/hse/incidents",
  observation: "/hse/observations",
  audit: "/hse/audits",
  inspection: "/hse/inspections",
  finding: "/hse/findings",
  sitespecificpermit: "/hse/compliance/organizational/site-specific-permits",
  companywidepermit: "/hse/compliance/organizational/company-wide-permits",
  eligibilitypermit: "/hse/compliance/personnel/eligibility-permits",
  competencepermit: "/hse/compliance/personnel/competence-permits",
};

export function routeFor(item: INotification): string {
  return MODULE_ROUTES[(item.modelType || "").toLowerCase()] ?? "/hse/actions";
}

function groupLabel(iso: string): string {
  const m = moment(iso);
  if (m.isSame(moment(), "day")) return "Today";
  if (m.isSame(moment().subtract(1, "day"), "day")) return "Yesterday";
  return "Earlier";
}

// Group by day over the accumulated (loaded) set; label only shown on group transitions.
export function groupNotifications(notifications: INotification[]) {
  const out: { label: string; item: INotification }[] = [];
  let last = "";
  for (const item of notifications) {
    const label = groupLabel(item.createdAt);
    out.push({ label: label !== last ? label : "", item });
    last = label;
  }
  return out;
}

// Defensive extraction: tolerate AutoWrapper / paginateable envelope variations.
export function extractNotifications(resp: unknown): INotification[] {
  const r = resp as any;
  return r?.data?.data ?? r?.result?.data ?? r?.data ?? r?.result ?? [];
}

export function extractTotal(resp: unknown): number {
  const r = resp as any;
  return (
    r?.data?.totalRecordCount ??
    r?.result?.totalRecordCount ??
    extractNotifications(resp).length
  );
}
